import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import axios from "axios";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  Chip,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Print as PrintIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import moment from "moment";
import EncounterCreate from "./EncounterCreate";
import TicketPrint from "./TicketPrint";
import ClinicalNotesList from "./ClinicalNotesList";

export default function TicketView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const headers = token ? { Authorization: `Token ${token}` } : {};
  const [tabValue, setTabValue] = useState(0);
  const [createEncounterOpen, setCreateEncounterOpen] = useState(false);
  const [printTicketOpen, setPrintTicketOpen] = useState(false);
  const [expandedEncounters, setExpandedEncounters] = useState({});
  const [queueUpdateLoading, setQueueUpdateLoading] = useState(false);
  const [ticketStatusLoading, setTicketStatusLoading] = useState(false);

  const { data: ticket, isLoading, isError, error, refetch: refetchTicket } = useFetchObjects(
    ["ticket", id],
    `tickets/${id}/`,
    token
  );

  const { data: encountersData } = useFetchObjects(
    ["encounters", "ticket", id],
    `encounters/?ticket=${id}`,
    token
  );

  const { data: queueEntriesData, refetch: refetchQueueEntries } = useFetchObjects(
    ["queue-entries", "ticket", id],
    `queue-entries/?ticket=${id}&page_size=100`,
    token
  );

  const encounters = encountersData?.results || [];
  const queueEntries = queueEntriesData?.results || queueEntriesData || [];
  const latestQueueEntry = useMemo(
    () => (Array.isArray(queueEntries) && queueEntries.length ? queueEntries[0] : null),
    [queueEntries]
  );

  const getTicketStatusColor = (status) => {
    switch (status) {
      case 1:
        return "success";
      case 2:
        return "info";
      case 3:
        return "error";
      default:
        return "default";
    }
  };

  const getQueueStatusColor = (status) => {
    switch (status) {
      case 1:
        return "default";
      case 2:
        return "primary";
      case 3:
        return "success";
      case 4:
        return "error";
      default:
        return "default";
    }
  };

  const updateTicketStatus = async (status) => {
    setTicketStatusLoading(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000/api"}/tickets/${id}/`,
        { status },
        { headers }
      );
      await refetchTicket();
    } finally {
      setTicketStatusLoading(false);
    }
  };

  const updateQueueStatus = async (queueEntryId, status) => {
    setQueueUpdateLoading(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000/api"}/queue-entries/${queueEntryId}/`,
        { status },
        { headers }
      );
      await refetchQueueEntries();
    } finally {
      setQueueUpdateLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !ticket) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || "Failed to load ticket"}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/reception/ticketing/tickets")}
              sx={{ mb: 1 }}
            >
              Back to Tickets
            </Button>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {t("reception.visitTicket", { defaultValue: "Visit Ticket" })}: {ticket.ticket_number}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => navigate(`/finance/payment-collection?ticket=${ticket.id}`)}
            >
              {t("reception.collectFees", "Collect fees")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => setPrintTicketOpen(true)}
            >
              Print Ticket
            </Button>
            <Chip
              label={ticket.status_display}
              color={getTicketStatusColor(ticket.status)}
              sx={{ height: 32 }}
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Patient
                </Typography>
                <Typography variant="h6">{ticket.patient_name_display}</Typography>
                {ticket.patient_mrn_display && (
                  <Typography variant="body2" color="text.secondary">
                    MRN: {ticket.patient_mrn_display}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Department
                </Typography>
                <Typography variant="h6">
                  {ticket.department_name_display || "N/A"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Visit Date
                </Typography>
                <Typography variant="h6">
                  {moment(ticket.visit_date).format("YYYY-MM-DD HH:mm")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Created By
                </Typography>
                <Typography variant="h6">
                  {ticket.created_by_name_display || "System"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Queue
                </Typography>
                <Typography variant="h6">
                  {latestQueueEntry ? `#${latestQueueEntry.queue_number}` : "No queue entry"}
                </Typography>
                {latestQueueEntry && (
                    <Chip
                      label={latestQueueEntry.status_display}
                      color={getQueueStatusColor(latestQueueEntry.status)}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                )}
              </CardContent>
            </Card>
          </Grid>

          {ticket.notes && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Notes
                  </Typography>
                  <Typography variant="body1">{ticket.notes}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
            Ticket Actions
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => updateTicketStatus(1)}
              disabled={ticketStatusLoading}
            >
              Mark Active
            </Button>
            <Button
              variant="outlined"
              color="info"
              onClick={() => updateTicketStatus(2)}
              disabled={ticketStatusLoading}
            >
              Mark Completed
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => updateTicketStatus(3)}
              disabled={ticketStatusLoading}
            >
              Mark Cancelled
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
            Queue Entries
          </Typography>
          {queueEntries.length === 0 ? (
            <Alert severity="info">
              No queue entries found for this ticket.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Queue #</TableCell>
                    <TableCell>Doctor</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queueEntries.map((queueEntry) => (
                    <TableRow key={queueEntry.id}>
                      <TableCell>#{queueEntry.queue_number}</TableCell>
                      <TableCell>{queueEntry.doctor_name_display || "N/A"}</TableCell>
                      <TableCell>{queueEntry.department_name_display || "N/A"}</TableCell>
                      <TableCell>
                          <Chip
                            label={queueEntry.status_display}
                          color={getQueueStatusColor(queueEntry.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
                          <Button size="small" onClick={() => updateQueueStatus(queueEntry.id, 1)} disabled={queueUpdateLoading}>
                            Waiting
                          </Button>
                          <Button size="small" onClick={() => updateQueueStatus(queueEntry.id, 2)} disabled={queueUpdateLoading}>
                            In Progress
                          </Button>
                          <Button size="small" color="success" onClick={() => updateQueueStatus(queueEntry.id, 3)} disabled={queueUpdateLoading}>
                            Completed
                          </Button>
                          <Button size="small" color="error" onClick={() => updateQueueStatus(queueEntry.id, 4)} disabled={queueUpdateLoading}>
                            Cancelled
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label={`Encounters (${encounters.length})`} />
            </Tabs>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateEncounterOpen(true)}
              sx={{ ml: 2 }}
            >
              Create Encounter
            </Button>
          </Box>

          {tabValue === 0 && (
            <Box sx={{ mt: 2 }}>
              {encounters.length === 0 ? (
                <Alert 
                  severity="info"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setCreateEncounterOpen(true)}
                    >
                      Create One
                    </Button>
                  }
                >
                  No encounters found for this ticket
                </Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Provider</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Notes</strong></TableCell>
                        <TableCell><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {encounters.map((encounter) => (
                        <React.Fragment key={encounter.id}>
                          <TableRow hover>
                            <TableCell>{encounter.encounter_type_display}</TableCell>
                            <TableCell>
                              {moment(encounter.encounter_date).format("YYYY-MM-DD HH:mm")}
                            </TableCell>
                            <TableCell>{encounter.provider_name_display || "N/A"}</TableCell>
                            <TableCell>
                              <Chip
                                label={encounter.status_display}
                                size="small"
                                color={encounter.status === 2 ? "success" : "default"}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={`${encounter.clinical_notes_count || 0} notes`}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={() => {
                                  setExpandedEncounters((prev) => ({
                                    ...prev,
                                    [encounter.id]: !prev[encounter.id],
                                  }));
                                }}
                                sx={{ mr: 1 }}
                              >
                                {expandedEncounters[encounter.id] ? "Hide Notes" : "View Notes"}
                              </Button>
                              <Button
                                size="small"
                                onClick={() => navigate(`/reception/ticketing/encounters/patient/${encounter.patient}`)}
                              >
                                Timeline
                              </Button>
                            </TableCell>
                          </TableRow>
                          {expandedEncounters[encounter.id] && (
                            <TableRow>
                              <TableCell colSpan={6} sx={{ py: 3, bgcolor: "background.default" }}>
                                <ClinicalNotesList encounter={encounter} />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Create Encounter Dialog */}
      {ticket && (
        <EncounterCreate
          open={createEncounterOpen}
          onClose={() => setCreateEncounterOpen(false)}
          ticket={ticket}
          patient={ticket.patient ? { id: ticket.patient } : null}
        />
      )}

      {/* Print Ticket Dialog */}
      <Dialog
        open={printTicketOpen}
        onClose={() => setPrintTicketOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <TicketPrint
            ticket={ticket}
            onClose={() => setPrintTicketOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
